import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Sun, 
  Map, 
  Camera, 
  BarChart3, 
  FileText, 
  Zap, 
  Globe, 
  Shield,
  ArrowRight,
  Play,
  CheckCircle,
  Star,
  Users,
  TrendingUp
} from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import toast from 'react-hot-toast';

const HomePage = () => {
  const [currentFeature, setCurrentFeature] = useState(0);
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const [ref1, inView1] = useInView({ threshold: 0.3, triggerOnce: true });
  const [ref2, inView2] = useInView({ threshold: 0.3, triggerOnce: true });
  const [ref3, inView3] = useInView({ threshold: 0.3, triggerOnce: true });

  const features = [
    {
      icon: Map,
      title: 'Cartographie Interactive',
      description: 'Délimitez votre toiture avec précision grâce à notre outil de cartographie avancé',
      color: 'from-sky-400 to-sky-600'
    },
    {
      icon: Camera,
      title: 'Intégration Drone',
      description: 'Importez vos prises de vue aériennes pour une analyse ultra-précise',
      color: 'from-green-400 to-green-600'
    },
    {
      icon: BarChart3,
      title: 'Dimensionnement Automatique',
      description: 'Calcul intelligent de la disposition optimale des panneaux photovoltaïques',
      color: 'from-solar-400 to-solar-600'
    },
    {
      icon: Zap,
      title: 'Estimation Production',
      description: 'Prévisions précises de production basées sur les données météorologiques',
      color: 'from-purple-400 to-purple-600'
    }
  ];

  const stats = [
    { number: 1500, suffix: '+', label: 'Projets Réalisés', icon: CheckCircle },
    { number: 98, suffix: '%', label: 'Satisfaction Client', icon: Star },
    { number: 500, suffix: '+', label: 'Professionnels', icon: Users },
    { number: 25, suffix: 'MW', label: 'Puissance Installée', icon: TrendingUp }
  ];

  const benefits = [
    'Gain de temps de 70% sur les études',
    'Précision de mesure au centimètre près',
    'Intégration API météo en temps réel',
    'Export PDF professionnel automatique',
    'Gestion multi-utilisateurs',
    'Support technique 24/7'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  const handleDemo = () => {
    toast.success('🎬 Démo en cours de chargement...', {
      duration: 3000,
      icon: '🚀',
    });
    // Ici on pourrait rediriger vers une page de démo ou ouvrir une modal
    setTimeout(() => {
      toast('✨ Découvrez toutes les fonctionnalités de ProtoSunny !', {
        duration: 4000,
        icon: '💡',
      });
    }, 1000);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background animé */}
        <motion.div
          style={{ y }}
          className="absolute inset-0 bg-gradient-to-br from-sky-900 via-purple-900 to-solar-900"
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Logo animé */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="mx-auto w-24 h-24 bg-gradient-to-r from-solar-400 to-solar-600 rounded-full flex items-center justify-center shadow-2xl"
            >
              <Sun className="h-12 w-12 text-white" />
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-display font-bold text-white leading-tight">
              <span className="bg-gradient-to-r from-solar-400 via-sky-400 to-green-400 bg-clip-text text-transparent">
                ProtoSunny
              </span>
              <br />
              <span className="text-3xl md:text-4xl font-light">
                L'avenir du photovoltaïque
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              Plateforme professionnelle de dimensionnement photovoltaïque avec cartographie interactive, 
              intégration drone et estimation de production en temps réel.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/register" className="button-primary text-lg px-8 py-4">
                  Commencer gratuitement
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDemo}
                className="button-secondary text-lg px-8 py-4 flex items-center space-x-2"
              >
                <Play className="h-5 w-5" />
                <span>Voir la démo</span>
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section ref={ref1} className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView1 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">
              Fonctionnalités <span className="text-solar-400">Révolutionnaires</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Découvrez les outils qui transforment votre approche du dimensionnement photovoltaïque
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView1 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className={`glass-effect rounded-2xl p-8 text-center card-hover ${
                  currentFeature === index ? 'ring-2 ring-solar-400' : ''
                }`}
              >
                <div className={`w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r ${feature.color} flex items-center justify-center`}>
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">{feature.title}</h3>
                <p className="text-white/70">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={ref2} className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView2 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView2 ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="glass-effect rounded-2xl p-8 card-hover">
                  <stat.icon className="h-12 w-12 text-solar-400 mx-auto mb-4" />
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                    <CountUp end={stat.number} duration={2.5} delay={index * 0.2} />
                    {stat.suffix}
                  </div>
                  <p className="text-white/70 text-lg">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={ref3} className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView3 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-8">
                Pourquoi choisir <span className="text-solar-400">ProtoSunny</span> ?
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView3 ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                    <span className="text-white/90 text-lg">{benefit}</span>
                  </motion.div>
                ))}
              </div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView3 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="mt-8"
              >
                <Link to="/pricing" className="button-primary text-lg px-8 py-4">
                  Voir les tarifs
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView3 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="glass-effect rounded-2xl p-8">
                <div className="aspect-video bg-gradient-to-br from-solar-400/20 to-sky-400/20 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Globe className="h-16 w-16 text-solar-400 mx-auto mb-4" />
                    <p className="text-white/70">Interface interactive</p>
                  </div>
                </div>
              </div>
              
              {/* Floating elements */}
              <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-r from-solar-400 to-solar-600 rounded-full opacity-20"
              />
              <motion.div
                animate={{ y: [0, 20, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-r from-sky-400 to-sky-600 rounded-full opacity-20"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="glass-effect rounded-3xl p-12"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">
              Prêt à révolutionner vos projets photovoltaïques ?
            </h2>
            <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
              Rejoignez des centaines de professionnels qui font confiance à ProtoSunny pour leurs études photovoltaïques.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/register" className="button-primary text-lg px-8 py-4">
                  Commencer maintenant
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="button-secondary text-lg px-8 py-4">
                  Voir les tarifs
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 